import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ArrowTopRightOnSquareIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { dietService } from '../../services/dietService';
import { ingredientService } from '../../services/ingredientService';
import { measureUnitService } from '../../services/measureUnitService';
import { lookupTableService } from '../../services/lookupTableService';
import { RecipePlate, CreateRecipePlateRequest, UpdateRecipePlateRequest, Recipe, Diet } from '../../types/diet';
import { Ingredient } from '../../types/ingredient';

interface Props {
  plate?: RecipePlate | null;
  onClose: () => void;
  onSuccess: () => void;
  prefill?: { desc?: string; dietId?: string; ksId?: string; unitId?: string };
  lockTopFields?: boolean;
  profileId?: string; // when provided, save items into this profile
}

const SIZE_COLS = [
  { key: 'xs', label: 'X Small' },
  { key: 's', label: 'Small' },
  { key: 'm', label: 'Medium' },
  { key: 'l', label: 'Large' },
  { key: 'xl', label: 'Extra Large' },
  { key: 'xxl', label: 'XX Large' },
] as const;

type SizeKey = typeof SIZE_COLS[number]['key'];

type RowState = {
  id?: string; // existing item ID if editing
  itemType: 'RECIPE' | 'INGREDIENT';
  recipeId: string;
  ingredientId: string;
  sizes: Record<SizeKey, { weight: string; unitId: string; cal: string }>;
};


const RecipePlateModal: React.FC<Props> = ({ plate, onClose, onSuccess, prefill, lockTopFields, profileId }) => {
  const [error, setError] = useState<string | null>(null);
  const [recipeSearchQueries, setRecipeSearchQueries] = useState<Record<number, string>>({});
  const [ingredientSearchQueries, setIngredientSearchQueries] = useState<Record<number, string>>({});
  const isEditing = !!plate;

  const { data: recipesData } = useQuery({
    queryKey: ['recipes', 'for-select'],
    queryFn: () => dietService.getRecipes({ page: 1, limit: 1000 }),
  });

  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients', 'for-select'],
    queryFn: () => ingredientService.getIngredients({ page: 1, limit: 1000, status: 'ACTIVE' }),
  });

  const { data: muData } = useQuery({
    queryKey: ['measure-units', 'for-select'],
    queryFn: () => measureUnitService.getMeasureUnits(),
  });

  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-select'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000, status: 'ACTIVE' as any }),
  });

  const { data: ksData } = useQuery({
    queryKey: ['lookup', 'kitchen-sections'],
    queryFn: () => lookupTableService.getLookupTables({ page: 1, limit: 1000, category: 'KITCHEN_SECTION', status: 'ACTIVE' }),
  });

  // Load existing items if editing a profile
  const { data: existingItemsData } = useQuery({
    queryKey: ['recipe-plate-profile-items', profileId],
    enabled: !!profileId,
    queryFn: () => dietService.getRecipePlateProfileItems(profileId!),
  });

  const makeEmptyRow = (): RowState => ({
    itemType: 'RECIPE',
    recipeId: '',
    ingredientId: '',
    sizes: {
      xs: { weight: '0', unitId: '', cal: '0' },
      s: { weight: '0', unitId: '', cal: '0' },
      m: { weight: '0', unitId: '', cal: '0' },
      l: { weight: '0', unitId: '', cal: '0' },
      xl: { weight: '0', unitId: '', cal: '0' },
      xxl: { weight: '0', unitId: '', cal: '0' },
    },
  });

  const [rows, setRows] = useState<RowState[]>([makeEmptyRow()]);
  const [statusSel, setStatusSel] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [instructions, setInstructions] = useState('');
  const [notes, setNotes] = useState('');

  // Top-level fields (Create mode)
  const [desc, setDesc] = useState(prefill?.desc || '');
  const [dietId, setDietId] = useState(prefill?.dietId || '');
  const [ksId, setKsId] = useState(prefill?.ksId || '');
  const [unitId, setUnitId] = useState(prefill?.unitId || '');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateRecipePlateRequest>({
    defaultValues: {
      name: plate?.name || '',
      description: plate?.description || '',

      recipeId: plate?.recipeId || '',
      plateSize: plate ? (Math.abs(((plate as any).quantity ?? 1) - 1) < 0.2 ? 'small' : Math.abs(((plate as any).quantity ?? 1.5) - 2) < 0.2 ? 'large' : 'medium') : 'medium',
      status: (plate as any)?.status || 'ACTIVE',
      calories: (plate as any)?.calories ?? 0,
      protein: (plate as any)?.protein ?? 0,
      carbs: (plate as any)?.carbs ?? 0,
      fat: (plate as any)?.fat ?? 0,
    }
  });

  useEffect(() => { if (plate) reset({ name: plate.name, description: plate.description || '', recipeId: plate.recipeId, plateSize: (Math.abs(((plate as any).quantity ?? 1) - 1) < 0.2 ? 'small' : Math.abs(((plate as any).quantity ?? 1.5) - 2) < 0.2 ? 'large' : 'medium'), status: (plate as any)?.status || 'ACTIVE', calories: (plate as any)?.calories ?? 0, protein: (plate as any)?.protein ?? 0, carbs: (plate as any)?.carbs ?? 0, fat: (plate as any)?.fat ?? 0 }); }, [plate, reset]);

  // Load existing items when editing a profile
  useEffect(() => {
    if (profileId && existingItemsData?.items && Array.isArray(existingItemsData.items)) {
      // Group items by recipe/ingredient to reconstruct the size matrix
      const itemMap = new Map<string, any[]>();

      existingItemsData.items.forEach((item: any) => {
        const key = item.recipeId || item.ingredientId;
        if (!itemMap.has(key)) {
          itemMap.set(key, []);
        }
        itemMap.get(key)!.push(item);
      });

      // Convert grouped items to rows
      const loadedRows = Array.from(itemMap.values()).map((items: any[]) => {
        const firstItem = items[0];
        const row: RowState = {
          id: items.map(i => i.id).join(','), // Store all IDs
          itemType: (firstItem.recipeId ? 'RECIPE' : 'INGREDIENT') as 'RECIPE' | 'INGREDIENT',
          recipeId: firstItem.recipeId || '',
          ingredientId: firstItem.ingredientId || '',
          sizes: {
            xs: { weight: '0', unitId: '', cal: '0' },
            s: { weight: '0', unitId: '', cal: '0' },
            m: { weight: '0', unitId: '', cal: '0' },
            l: { weight: '0', unitId: '', cal: '0' },
            xl: { weight: '0', unitId: '', cal: '0' },
            xxl: { weight: '0', unitId: '', cal: '0' },
          },
        };

        // Populate sizes with data from items using the size field
        items.forEach((item) => {
          const sizeKey = (item.size || 'xs') as SizeKey;
          row.sizes[sizeKey] = {
            weight: String(item.quantity || 0),
            unitId: item.measureUnitId || '',
            cal: String(item.calories || 0),
          };
        });

        return row;
      });

      if (loadedRows.length > 0) {
        setRows(loadedRows);
      }
    }
  }, [profileId, existingItemsData]);

  const createMutation = useMutation({
    mutationFn: (data: CreateRecipePlateRequest) => dietService.createRecipePlate(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create recipe plate'),
  });

  const createAllMutation = useMutation({
    mutationFn: async (payloads: CreateRecipePlateRequest[]) => {
      await Promise.all(payloads.map((p) => dietService.createRecipePlate(p)));
    },
    onSuccess: () => onSuccess(),
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to create recipe plates'),
  });
  const addItemsMutation = useMutation({
    mutationFn: (items: any[]) => dietService.addRecipePlateProfileItems(profileId!, items as any),
    onSuccess: () => onSuccess(),
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to save items to profile'),
  });

  const updateItemMutation = useMutation({
    mutationFn: (data: { id: string; recipeId?: string; ingredientId?: string }) => dietService.updateRecipePlate(data.id, data as any),
    onSuccess: () => onSuccess(),
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to update item'),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteRecipePlate(id),
    onSuccess: () => onSuccess(),
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to delete item'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateRecipePlateRequest) => dietService.updateRecipePlate(plate!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update recipe plate'),
  });

  const onSubmit = (data: CreateRecipePlateRequest) => { setError(null); isEditing ? updateMutation.mutate(data) : createMutation.mutate(data); };
  const isLoading = createMutation.isPending || updateMutation.isPending || createAllMutation.isPending || addItemsMutation.isPending || updateItemMutation.isPending || deleteItemMutation.isPending;

  const recipes: Recipe[] = (recipesData as any)?.recipes || [];
  const ingredients: Ingredient[] = (ingredientsData as any)?.ingredients || [];
  const measureUnits = (muData as any)?.measureUnits || [];
  const diets: Diet[] = (dietsData as any)?.diets || [];
  const kitchenSections: any[] = (ksData as any)?.lookupTables || [];

  // Default unit to grams if available
  useEffect(() => {
    if (!unitId && Array.isArray(measureUnits) && measureUnits.length) {
      const grams = measureUnits.find((u: any) => (u.abbreviation || '').toLowerCase() === 'g');
      if (grams) setUnitId(grams.id);
    }
  }, [measureUnits, unitId]);

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Recipe Plate' : 'Create Recipe Plates'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}

                {isEditing ? (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <label className="form-label">Name *</label>
                      <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter plate name" />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Description</label>
                      <textarea {...register('description')} className="form-input" rows={2} placeholder="Optional description" />
                    </div>
                    <div>
                      <label className="form-label">Recipe *</label>
                      <select {...register('recipeId', { required: 'Recipe is required' })} className="form-input">
                        <option value="">Select recipe</option>
                        {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Calories</label>
                        <input type="number" step="0.01" {...register('calories')} className="form-input" placeholder="e.g. 450" />
                      </div>
                      <div>
                        <label className="form-label">Protein (g)</label>
                        <input type="number" step="0.01" {...register('protein')} className="form-input" placeholder="e.g. 30" />
                      </div>
                      <div>
                        <label className="form-label">Carbs (g)</label>
                        <input type="number" step="0.01" {...register('carbs')} className="form-input" placeholder="e.g. 50" />
                      </div>
                      <div>
                        <label className="form-label">Fat (g)</label>
                        <input type="number" step="0.01" {...register('fat')} className="form-input" placeholder="e.g. 15" />
                      </div>
                    </div>

                    {errors.recipeId && <p className="form-error">{errors.recipeId.message}</p>}
                    <div>
                      <label className="form-label">Plate Size *</label>
                      <select {...register('plateSize', { required: 'Plate size is required' })} className="form-input">
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="form-input">
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                      <Button type="submit" loading={isLoading}>Update</Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    {/* Top fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Description *</label>
                        <input className="form-input h-11 text-base" value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Enter description" disabled={!!lockTopFields} />
                      </div>
                      <div>
                        <label className="form-label">Diet *</label>
                        <select className="form-input h-11 text-base" value={dietId} onChange={e=>setDietId(e.target.value)} disabled={!!lockTopFields}>
                          <option value="">Select diet</option>
                          {diets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Kitchen Section *</label>
                        <select className="form-input h-11 text-base" value={ksId} onChange={e=>setKsId(e.target.value)} disabled={!!lockTopFields}>
                          <option value="">Select kitchen section</option>
                          {kitchenSections.map((k:any) => <option key={k.id} value={k.id}>{k.value}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Unit *</label>
                        <select className="form-input h-11 text-base" value={unitId} onChange={e=>setUnitId(e.target.value)} disabled={!!lockTopFields}>
                          <option value="">Select unit</option>
                          {measureUnits.map((u:any) => <option key={u.id} value={u.id}>{u.abbreviation || u.name}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">Add one or more rows, then fill sizes (Weight/Unit/Calories).</div>
                      <Button type="button" variant="outline" onClick={() => setRows([...rows, makeEmptyRow()])}>+ Add Row</Button>
                    </div>
                    <div className="overflow-auto border rounded-lg">
                      <table className="min-w-full table-auto whitespace-nowrap text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2"></th>
                            <th className="px-3 py-2 text-left">Item Type</th>
                            <th className="px-3 py-2 text-left">Recipe</th>
                            <th className="px-3 py-2 text-left">Ingredients</th>
                            {SIZE_COLS.map(sc => (
                              <th key={sc.key} className="px-3 py-2 text-center" colSpan={3}>{sc.label}</th>
                            ))}
                          </tr>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2"></th>
                            <th className="px-3 py-2"></th>
                            <th className="px-3 py-2"></th>
                            <th className="px-3 py-2"></th>
                            {SIZE_COLS.map(sc => (
                              <React.Fragment key={`sub-${sc.key}`}>
                                <th className="px-3 py-2 text-center">Weight</th>
                                <th className="px-3 py-2 text-center">Unit</th>
                                <th className="px-3 py-2 text-center">Calories</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (row.id) {
                                      deleteItemMutation.mutate(row.id);
                                    } else {
                                      setRows(rows.filter((_, i) => i !== idx));
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                  disabled={deleteItemMutation.isPending}
                                >
                                  ×
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  className="form-input h-11 text-base min-w-36"
                                  value={row.itemType}
                                  onChange={(e)=>{ const val = e.target.value as 'RECIPE'|'INGREDIENT'; const nr=[...rows]; nr[idx].itemType = val; if (val==='RECIPE'){ nr[idx].ingredientId=''; } else { nr[idx].recipeId=''; } setRows(nr); }}
                                >
                                  <option value="RECIPE">Recipe</option>
                                  <option value="INGREDIENT">Ingredient</option>
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Combobox
                                    value={row.recipeId}
                                    onChange={(value) => {
                                      const nr = [...rows];
                                      nr[idx].recipeId = value;
                                      setRows(nr);
                                    }}
                                    disabled={row.itemType !== 'RECIPE'}
                                  >
                                    {({ open }) => {
                                      const query = recipeSearchQueries[idx] || '';
                                      const filteredRecipes = query === ''
                                        ? recipes
                                        : recipes.filter((r) =>
                                            r.name.toLowerCase().includes(query.toLowerCase())
                                          );

                                      return (
                                        <div className="relative min-w-[16rem]">
                                          <Combobox.Input
                                            className="form-input h-11 text-base w-full pr-10"
                                            displayValue={(recipeId: string) =>
                                              recipes.find((r) => r.id === recipeId)?.name || ''
                                            }
                                            onChange={(event) => setRecipeSearchQueries(prev => ({ ...prev, [idx]: event.target.value }))}
                                            placeholder="Select recipe"
                                          />
                                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                          </Combobox.Button>
                                          <Transition
                                            show={open}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                          >
                                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                              {filteredRecipes.length === 0 && query !== '' ? (
                                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                                  Nothing found.
                                                </div>
                                              ) : (
                                                filteredRecipes.map((recipe) => (
                                                  <Combobox.Option
                                                    key={recipe.id}
                                                    value={recipe.id}
                                                    className={({ active }) =>
                                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                                        active ? 'bg-primary-600 text-white' : 'text-gray-900'
                                                      }`
                                                    }
                                                  >
                                                    {({ selected, active }) => (
                                                      <>
                                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                          {recipe.name}
                                                        </span>
                                                        {selected ? (
                                                          <span
                                                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                              active ? 'text-white' : 'text-primary-600'
                                                            }`}
                                                          >
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                          </span>
                                                        ) : null}
                                                      </>
                                                    )}
                                                  </Combobox.Option>
                                                ))
                                              )}
                                            </Combobox.Options>
                                          </Transition>
                                        </div>
                                      );
                                    }}
                                  </Combobox>
                                  <a href="/setup/recipes" target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-800" title="Open Recipes page">
                                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                                  </a>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Combobox
                                    value={row.ingredientId}
                                    onChange={(value) => {
                                      const nr = [...rows];
                                      nr[idx].ingredientId = value;
                                      setRows(nr);
                                    }}
                                    disabled={row.itemType !== 'INGREDIENT'}
                                  >
                                    {({ open }) => {
                                      const query = ingredientSearchQueries[idx] || '';
                                      const filteredIngredients = query === ''
                                        ? ingredients
                                        : ingredients.filter((i) =>
                                            i.name.toLowerCase().includes(query.toLowerCase())
                                          );

                                      return (
                                        <div className="relative min-w-[16rem]">
                                          <Combobox.Input
                                            className="form-input h-11 text-base w-full pr-10"
                                            displayValue={(ingredientId: string) =>
                                              ingredients.find((i) => i.id === ingredientId)?.name || ''
                                            }
                                            onChange={(event) => setIngredientSearchQueries(prev => ({ ...prev, [idx]: event.target.value }))}
                                            placeholder="Select ingredient"
                                          />
                                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                          </Combobox.Button>
                                          <Transition
                                            show={open}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                          >
                                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                              {filteredIngredients.length === 0 && query !== '' ? (
                                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                                  Nothing found.
                                                </div>
                                              ) : (
                                                filteredIngredients.map((ingredient) => (
                                                  <Combobox.Option
                                                    key={ingredient.id}
                                                    value={ingredient.id}
                                                    className={({ active }) =>
                                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                                        active ? 'bg-primary-600 text-white' : 'text-gray-900'
                                                      }`
                                                    }
                                                  >
                                                    {({ selected, active }) => (
                                                      <>
                                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                          {ingredient.name}
                                                        </span>
                                                        {selected ? (
                                                          <span
                                                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                              active ? 'text-white' : 'text-primary-600'
                                                            }`}
                                                          >
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                          </span>
                                                        ) : null}
                                                      </>
                                                    )}
                                                  </Combobox.Option>
                                                ))
                                              )}
                                            </Combobox.Options>
                                          </Transition>
                                        </div>
                                      );
                                    }}
                                  </Combobox>
                                  <a href="/setup/ingredients" target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-800" title="Open Ingredients page">
                                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                                  </a>
                                </div>
                              </td>
                              {SIZE_COLS.map(sc => (
                                <React.Fragment key={sc.key}>
                                  <td className="px-3 py-2">
                                    <input type="number" className="form-input h-11 text-base w-24" value={row.sizes[sc.key].weight} onChange={e=>{ const nr=[...rows]; nr[idx].sizes[sc.key].weight = e.target.value; setRows(nr); }} placeholder="0" />
                                  </td>
                                  <td className="px-3 py-2">
                                    <select className="form-input h-11 text-base w-28" value={row.sizes[sc.key].unitId} onChange={e=>{ const nr=[...rows]; nr[idx].sizes[sc.key].unitId = e.target.value; setRows(nr); }}>
                                      <option value="">Select</option>
                                      {measureUnits.map((u:any) => <option key={u.id} value={u.id}>{u.abbreviation || u.name}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-3 py-2">
                                    <input type="number" className="form-input h-11 text-base w-24" value={row.sizes[sc.key].cal} onChange={e=>{ const nr=[...rows]; nr[idx].sizes[sc.key].cal = e.target.value; setRows(nr); }} placeholder="0" />
                                  </td>
                                </React.Fragment>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Instructions</label>
                        <textarea className="form-input h-24 text-base" rows={4} value={instructions} onChange={e=>setInstructions(e.target.value)} />
                      </div>
                      <div>
                        <label className="form-label">Notes</label>
                        <textarea className="form-input h-24 text-base" rows={4} value={notes} onChange={e=>setNotes(e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Status</label>
                        <select className="form-input h-11 text-base" value={statusSel} onChange={e=>setStatusSel(e.target.value as any)}>
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Current Date</label>
                        <input className="form-input h-11 text-base" value={new Date().toISOString().replace('T',' ').substring(0,19)} readOnly />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-2">
                      <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                      <Button type="button" loading={isLoading} onClick={() => {
                        setError(null);
                        if (!desc.trim()) { setError('Description is required'); return; }
                        if (!dietId) { setError('Diet is required'); return; }
                        if (!ksId) { setError('Kitchen Section is required'); return; }
                        if (!unitId) { setError('Unit is required'); return; }
                        const payloads: CreateRecipePlateRequest[] = [];
                        let hasValidRow = false;
                        rows.forEach((r) => {
                          const isRecipe = r.itemType === 'RECIPE';
                          const srcId = isRecipe ? r.recipeId : r.ingredientId;
                          // Respect mutually exclusive rule: only one is required based on itemType
                          if (!srcId) return;
                          hasValidRow = true;
                          SIZE_COLS.forEach(sc => {
                            const s = r.sizes[sc.key];
                            const weightNum = parseFloat(s.weight);
                            if (!isNaN(weightNum) && weightNum >= 0) {
                              payloads.push({
                                name: desc.trim(),
                                recipeId: isRecipe ? r.recipeId : undefined,
                                ingredientId: !isRecipe ? r.ingredientId : undefined,
                                dietId,
                                kitchenSectionId: ksId,
                                quantity: weightNum,
                                measureUnitId: s.unitId || unitId,
                                calories: s.cal ? Number(s.cal) : 0,
                                size: sc.key, // Add size
                                status: statusSel,
                              });
                            }
                          });
                        });
                        if (!hasValidRow) { setError('Please select at least one Recipe or Ingredient'); return; }
                        if (payloads.length === 0) { setError('Please fill at least one size with Weight >= 0'); return; }
                        if (profileId) {
                          // Separate existing items (with id) from new items (without id)
                          const existingRows = rows.filter(r => r.id);

                          // Delete items that are no longer in the rows
                          // (This would require tracking which items were deleted)

                          // Add new items
                          const newPayloads = payloads.filter(p => {
                            const row = rows.find(r => !r.id && ((p.recipeId && r.recipeId === p.recipeId) || (p.ingredientId && r.ingredientId === p.ingredientId)));
                            return !!row;
                          });

                          // Update existing items
                          const updatePromises: Promise<any>[] = [];
                          existingRows.forEach(row => {
                            if (!row.id) return;

                            const rowPayloads = payloads.filter(p =>
                              (p.recipeId && row.recipeId === p.recipeId) ||
                              (p.ingredientId && row.ingredientId === p.ingredientId)
                            );

                            // Get the IDs from the comma-separated string
                            const ids = row.id.split(',');

                            // Update each size's record
                            rowPayloads.forEach((p, idx) => {
                              if (ids[idx]) {
                                updatePromises.push(
                                  dietService.updateRecipePlate(ids[idx], {
                                    quantity: p.quantity,
                                    measureUnitId: p.measureUnitId,
                                    calories: p.calories,
                                    protein: (p as any).protein,
                                    carbs: (p as any).carbs,
                                    fat: (p as any).fat,
                                    size: p.size,
                                    status: p.status,
                                  } as any)
                                );
                              }
                            });
                          });

                          if (newPayloads.length > 0 || updatePromises.length > 0) {
                            const items = newPayloads.map(p => ({
                              recipeId: p.recipeId,
                              ingredientId: p.ingredientId,
                              quantity: p.quantity,
                              measureUnitId: p.measureUnitId,
                              calories: p.calories,
                              protein: (p as any).protein,
                              carbs: (p as any).carbs,
                              fat: (p as any).fat,
                              size: p.size,
                              status: p.status,
                            })) as any;

                            if (updatePromises.length > 0) {
                              Promise.all(updatePromises).then(() => {
                                if (items.length > 0) {
                                  addItemsMutation.mutate(items);
                                } else {
                                  onSuccess();
                                }
                              }).catch((e) => setError(e?.message || 'Failed to update items'));
                            } else {
                              addItemsMutation.mutate(items);
                            }
                          } else {
                            // No items to save
                            onSuccess();
                          }
                        } else {
                          createAllMutation.mutate(payloads);
                        }
                      }}>Save</Button>
                    </div>
                  </div>
                )}

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RecipePlateModal;

